import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-icon.svg';
import { ReactComponent as Sync } from '../../../../assets/icons/sync.svg';
import { ReactComponent as Tag } from '../../../../assets/icons/tag.svg';
import { ReactComponent as Upload } from '../../../../assets/icons/upload-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  broadcastCpoTariffs,
  fetchCpoTariffFilters,
  fetchCpoTariffs,
  setSortData,
  updateCPOTariffsFilters,
} from '../../../../redux/slices/OCPI/CPOTariffsSlice';
import { RootState } from '../../../../redux/store';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import tariffApi from '../../../../utils/api/tariff';

/**
 * Builds a user-facing error message for a failed tariff sync request, distinguishing
 * between being offline, no response from the server, and a server error.
 * @param {any} error - The error thrown by the sync request.
 * @param {any} t - The translation function.
 * @returns {string} A user-facing error message.
 */
const resolveSyncDataErrorMessage = (error: any, t: any): string => {
  if (typeof window !== 'undefined' && !window.navigator.onLine) {
    return t('ocpi:offlineErrorMessage');
  }
  if (error?.response) {
    return (
      error.response.data?.message ||
      error.response.data?.status_message ||
      t('ocpi:failedToSyncTariffData')
    );
  }
  if (error?.request) {
    return t('ocpi:noResponseFromServer');
  }
  return error?.message || t('ocpi:failedToSyncTariffData');
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiPriceComponent {
  type: string;
  price: number;
  vat?: number;
  step_size?: number;
}

interface OcpiTariffElement {
  price_components?: OcpiPriceComponent[];
  restrictions?: { start_time?: string; end_time?: string };
}

interface OcpiTariff {
  id: string;
  currency?: string;
  type?: string;
  country_code?: string;
  party_id?: string;
  elements?: OcpiTariffElement[];
  start_date_time?: string;
  end_date_time?: string;
  last_updated?: string;
  _objectId?: string;
  _lastUpdated?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
}

// ── Tariff Drill-Down Modal ─────────────────────────────────────────────────────

/**
 * Renders the tariff drill-down modal showing raw price component detail.
 * @param {any} props The props.
 * @param {any} props.tariff The tariff details.
 * @param {any} props.onClose The close callback.
 */
function TariffDrillDownModal({
  tariff,
  onClose,
}: Readonly<{
  tariff: OcpiTariff;
  onClose: () => void;
}>) {
  const { t } = useTranslation(['ocpi']);
  const elements: OcpiTariffElement[] = tariff.elements || [];

  return (
    <ModalBox
      open
      header={false}
      notification
      title={null}
      closeModal={onClose}
      content={
        <div className="cpo-tariff-drilldown-modal">
          {/* Header */}
          <div className="cpo-drilldown-header">
            <div>
              <h3 className="cpo-drilldown-title">{tariff.id}</h3>
              <p className="cpo-tariff-drilldown-subtitle">
                {tariff.currency || '–'} {tariff.type ? `· ${tariff.type}` : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="cpo-drilldown-close">
              <CloseIcon width={30} height={30} />
            </button>
          </div>

          {/* Elements list */}
          <div className="cpo-drilldown-body">
            {elements.length === 0 ? (
              <div className="cpo-drilldown-empty">
                <Tag height={32} width={32} className="cpo-drilldown-empty-icon" />
                <p>{t('ocpi:noPriceElements')}</p>
              </div>
            ) : (
              <div className="cpo-tariff-element-list">
                {elements.map((element, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <pre key={index} className="cpo-tariff-element-card">
                    {JSON.stringify(element, null, 2)}
                  </pre>
                ))}
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

/**
 * CpoTariffsPanel component for viewing CPO OCPI tariffs.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 */
export default function CpoTariffsPanel({
  countryCode,
  partyId,
}: Readonly<{
  countryCode: string;
  partyId: string;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('ocpi');
  const {
    tariffs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.cpoTariffs);
  const [broadcasting, setBroadcasting] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<OcpiTariff | null>(null);
  const [syncingData, setSyncingData] = useState(false);
  const [syncDataSuccessMessage, setSyncDataSuccessMessage] = useState<string | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches CPO tariffs for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = useCallback(
    (data: ApiRequest) => {
      if (!loading) {
        dispatch(fetchCpoTariffs({ countryCode, partyId, ...data }));
      }
    },
    [loading, dispatch, countryCode, partyId],
  );

  useEffect(() => {
    dispatch(fetchCpoTariffs({ countryCode, partyId }));
  }, [countryCode, partyId, dispatch]);

  /**
   * Broadcasts all CPO tariffs to connected partners.
   */
  const handleBroadcast = async () => {
    setBroadcasting(true);
    try {
      await dispatch(broadcastCpoTariffs({ countryCode, partyId })).unwrap();
    } catch {
      // Notification already dispatched by the broadcastCpoTariffs thunk.
    } finally {
      setBroadcasting(false);
    }
  };

  interface TariffRow {
    id: string;
    currency: string;
    type: string;
    partyId: string;
    countryCode: string;
    price: string;
    validFrom: string;
    validTo: string;
    elementsCount: number;
    lastUpdated: string;
    _createdAt?: string;
    createdAt: string;
    raw: OcpiTariff;
  }

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof TariffRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {TariffRow} currentRow The current tariff row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: TariffRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:tariffId'),
      accessorKey: 'id' as keyof TariffRow,
      sortAllow: false,
      columnWidth: 260,
      /**
       * Renders the tariff ID in a code badge.
       * @param {any} row The tariff row.
       */
      customData: (row: TariffRow) => <code className="cpo-tariff-id-badge">{row.id}</code>,
    },
    {
      label: t('ocpi:currency'),
      accessorKey: 'currency' as keyof TariffRow,
      sortAllow: false,
    },
    // {
    //   label: t('ocpi:type'),
    //   accessorKey: 'type' as keyof TariffRow,
    //   sortAllow: false,
    // },
    {
      label: t('ocpi:partyId'),
      accessorKey: 'partyId' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:countryCode'),
      accessorKey: 'countryCode' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:price'),
      accessorKey: 'price' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:validFrom'),
      accessorKey: 'validFrom' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:validTo'),
      accessorKey: 'validTo' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:elements'),
      accessorKey: 'elementsCount' as keyof TariffRow,
      sortAllow: false,
      /**
       * Renders the price elements count badge.
       * @param {any} row The tariff row.
       */
      customData: (row: TariffRow) => (
        <span className="cpo-tariff-elements-badge">
          {row.elementsCount} {row.elementsCount !== 1 ? 'Elements' : 'Element'}{' '}
        </span>
      ),
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated'),
      accessorKey: 'lastUpdated' as keyof TariffRow,
      sortAllow: false,
    },
  ];

  /**
   * Summarizes the first price component across a tariff's elements.
   * @param {OcpiTariff} tariff The tariff.
   * @returns {string} A "TYPE price/unit currency" summary, or '–' if none exist.
   */
  const summarizePrice = (tariff: OcpiTariff): string => {
    const component = tariff.elements?.[0]?.price_components?.[0];
    if (!component) return '–';
    return `${component.type} ${component.price} ${tariff.currency || ''}`.trim();
  };

  const tableData: TariffRow[] = tariffs.map((tariff: OcpiTariff) => {
    const tariffId = tariff.id || tariff._objectId || '';
    const dateString = tariff.last_updated || tariff._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const createdAt =
      tariff._createdAt ||
      tariff.createdAt ||
      (tariff as any).created_at ||
      (tariff as any).created ||
      '';

    return {
      id: tariffId,
      currency: tariff.currency || '–',
      type: tariff.type || '–',
      partyId: tariff.party_id || '–',
      countryCode: tariff.country_code || '–',
      price: summarizePrice(tariff),
      validFrom: tariff.start_date_time ? new Date(tariff.start_date_time).toLocaleString() : '–',
      validTo: tariff.end_date_time ? new Date(tariff.end_date_time).toLocaleString() : '–',
      elementsCount: tariff.elements?.length ?? 0,
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: tariff,
    };
  });

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField: sortField ?? undefined, sortBy: sortBy ?? undefined });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Manually syncs tariff data with the OCPI gateway. Shows a success modal and
   * refreshes the tariffs list on success; errors (offline, no response, or a
   * server-side failure) are surfaced via the shared notification toast.
   */
  const handleSyncData = async () => {
    setSyncingData(true);
    try {
      const response = await tariffApi.syncOcpiTariffs();
      const rawMessage = response?.data?.message || response?.data?.status_message;
      let message = t('ocpi:tariffDataSyncedSuccessfully');
      if (rawMessage === 'Tariff sync started') {
        message = t('ocpi:tariffSyncStarted');
      } else if (rawMessage && rawMessage !== 'Tariff data synced successfully.') {
        message = rawMessage;
      }
      setSyncDataSuccessMessage(message);
      handleRefresh();
    } catch (error: any) {
      dispatch(
        setNotificationMessage({ message: resolveSyncDataErrorMessage(error, t), type: 'error' }),
      );
    } finally {
      setSyncingData(false);
    }
  };

  return (
    <div className="cpo-tariffs-panel">
      {/* ── Top bar ── */}
      <div className="cpo-tariffs-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:ocpiTariffs')}</h3>
          <p className="cpo-panel-subtitle">
            {tariffs.length > 0
              ? `${tariffs.length} ${t(
                  tariffs.length !== 1 ? 'ocpi:tariffCountPlural' : 'ocpi:tariffCountSingular',
                )} ${t('ocpi:inOcpiGateway')}`
              : t('ocpi:tariffsEmptySubtitle')}
          </p>
        </div>
      </div>

      {/* ── Tariffs Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateCPOTariffsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchCpoTariffFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedTariff(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
        rightTopContent={
          <div className="cpo-tariffs-actions">
            <MyButton
              label={syncingData ? t('ocpi:syncing') : t('ocpi:syncData')}
              svgIcon={<Sync />}
              buttonType="outline"
              onClickFunc={handleSyncData}
              disableButton={syncingData}
            />
            <MyButton
              buttonType="primary"
              label={broadcasting ? t('ocpi:broadcasting') : t('ocpi:broadcastAll')}
              svgIcon={<Upload />}
              onClickFunc={handleBroadcast}
              disableButton={broadcasting || tariffs.length === 0}
            />
          </div>
        }
      />

      {/* ── Drill-down modal ── */}
      {selectedTariff && (
        <TariffDrillDownModal tariff={selectedTariff} onClose={() => setSelectedTariff(null)} />
      )}

      {/* ── Sync data success modal ── */}
      <ModalBox
        open={Boolean(syncDataSuccessMessage)}
        header={false}
        notification
        title={null}
        closeModal={() => setSyncDataSuccessMessage(null)}
        content={
          <SuccessNotification
            title={t('ocpi:tariffDataSynced')}
            description={syncDataSuccessMessage || ''}
            onNotificationClose={() => setSyncDataSuccessMessage(null)}
          />
        }
      />
    </div>
  );
}
